import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MessageSquareText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TalkChatBlocks } from './TalkChatBlocks';
import { TalkChatComposer } from './TalkChatComposer';
import type {
  AdviceOption,
  ChatBlock,
  ChatImage,
  ToolExecution,
} from './types';

let blockIdCounter = 0;

function normalizeChatBlocks(blocks: ChatBlock[]): ChatBlock[] {
  const normalized: ChatBlock[] = [];
  let activeThinkingIndex = -1;

  const closeThinkingGroup = () => {
    if (activeThinkingIndex >= 0) {
      const current = normalized[activeThinkingIndex];
      if (
        current &&
        current.type === 'reasoning-summary' &&
        !current.collapsed
      ) {
        normalized[activeThinkingIndex] = { ...current, collapsed: true };
      }
    }
    activeThinkingIndex = -1;
  };

  for (const block of blocks) {
    if (block.type === 'reasoning-summary') {
      const entry = String(block.content || '').trim();
      if (!entry) continue;

      if (activeThinkingIndex >= 0) {
        const current = normalized[activeThinkingIndex];
        const currentEntries =
          Array.isArray(current.entries) && current.entries.length > 0
            ? current.entries
            : current.content
              ? [current.content]
              : [];
        if (!currentEntries.includes(entry)) {
          const nextEntries = [...currentEntries, entry];
          normalized[activeThinkingIndex] = {
            ...current,
            content: nextEntries[nextEntries.length - 1],
            entries: nextEntries,
          };
        }
        continue;
      }

      normalized.push({
        ...block,
        content: entry,
        entries: [entry],
      });
      activeThinkingIndex = normalized.length - 1;
      continue;
    }

    normalized.push(block);
    if (
      block.type === 'user' ||
      block.type === 'agent-text' ||
      block.type === 'error' ||
      block.type === 'advice'
    ) {
      closeThinkingGroup();
    }
  }

  return normalized;
}

function finalizeExecutionStatus(
  execution: ToolExecution,
  status: 'failed' | 'cancelled',
): ToolExecution {
  if (!execution || typeof execution !== 'object') return execution;
  if (execution.status !== 'running' && execution.status !== 'retrying') {
    return execution;
  }

  const endedAt = new Date().toISOString();
  const started = execution.startedAt ? Date.parse(execution.startedAt) : NaN;
  const ended = Date.parse(endedAt);
  const durationMs =
    Number.isFinite(started) && Number.isFinite(ended) && ended >= started
      ? ended - started
      : (execution.durationMs ?? null);

  return {
    ...execution,
    status,
    endedAt,
    durationMs,
    errorCode:
      execution.errorCode ||
      (status === 'cancelled' ? 'interrupted' : 'stream_closed'),
  };
}

interface NativeTalkChatProps {
  sessionId: string;
  activeChatSessionId: string | null;
  refreshKey?: number;
}

export function NativeTalkChat({
  sessionId,
  activeChatSessionId,
  refreshKey = 0,
}: NativeTalkChatProps) {
  const { t } = useTranslation('session');
  const apiBase = useMemo(() => `/api/acpx/sessions/${sessionId}`, [sessionId]);
  const historyUrl = useMemo(() => {
    const query = activeChatSessionId
      ? `?chatSessionId=${encodeURIComponent(activeChatSessionId)}`
      : '';
    return `${apiBase}/history${query}`;
  }, [activeChatSessionId, apiBase]);
  const [blocks, setBlocks] = useState<ChatBlock[]>([]);
  const [connected, setConnected] = useState(false);
  const [backendBusy, setBackendBusy] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [showLongTaskTip, setShowLongTaskTip] = useState(false);
  const [aiTipMessage, setAiTipMessage] = useState('');
  const [codingActions, setCodingActions] = useState<
    { tool: string; label: string }[]
  >([]);
  const [codingStats, setCodingStats] = useState<{
    reads: number;
    edits: number;
    commands: number;
  } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const streamBufferRef = useRef<{
    type: ChatBlock['type'];
    content: string;
    images?: ChatImage[];
  }>({
    type: 'agent-text',
    content: '',
    images: undefined,
  });
  const toolGroupRef = useRef<string[]>([]);
  const updateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longTaskTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const prevBackendBusyRef = useRef(false);
  const sendingRef = useRef(false);
  const toolActivityBlockIdRef = useRef<number | null>(null);
  const thinkingBlockIdRef = useRef<number | null>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [blocks, scrollToBottom]);

  useEffect(
    () => () => {
      if (longTaskTimerRef.current) clearTimeout(longTaskTimerRef.current);
    },
    [],
  );

  const resetLongTaskTip = useCallback(() => {
    if (longTaskTimerRef.current) {
      clearTimeout(longTaskTimerRef.current);
      longTaskTimerRef.current = null;
    }
    setShowLongTaskTip(false);
  }, []);

  const startLongTaskTipTimer = useCallback(() => {
    if (longTaskTimerRef.current) return;
    longTaskTimerRef.current = setTimeout(() => {
      setShowLongTaskTip(true);
      longTaskTimerRef.current = null;
    }, 4000);
  }, []);

  const refreshHealth = useCallback(async () => {
    try {
      const response = await fetch(`${apiBase}/health`, {
        credentials: 'include',
      });
      if (!response.ok) {
        setConnected(false);
        setBackendBusy(false);
        return;
      }
      const data = await response.json().catch(() => ({}));
      setConnected(true);
      setBackendBusy(Boolean(data?.busy));
    } catch {
      setConnected(false);
      setBackendBusy(false);
    }
  }, [apiBase]);

  const flushBuffer = useCallback(() => {
    const buffer = streamBufferRef.current;
    if (
      !buffer.content &&
      (!Array.isArray(buffer.images) || buffer.images.length === 0)
    )
      return;
    setBlocks((prev) => [
      ...prev,
      {
        id: ++blockIdCounter,
        type: buffer.type,
        content: buffer.content,
        collapsed: false,
        images:
          Array.isArray(buffer.images) && buffer.images.length > 0
            ? buffer.images
            : undefined,
      },
    ]);
    streamBufferRef.current = {
      type: 'agent-text',
      content: '',
      images: undefined,
    };
  }, []);

  const flushToolGroup = useCallback(() => {
    if (toolGroupRef.current.length === 0) return;
    const tools = [...toolGroupRef.current];
    toolGroupRef.current = [];
    setBlocks((prev) => [
      ...prev,
      {
        id: ++blockIdCounter,
        type: 'tool-group',
        content: '',
        tools,
      },
    ]);
  }, []);

  const clearTransientActivity = useCallback(
    (status?: 'failed' | 'cancelled') => {
      toolGroupRef.current = [];
      toolActivityBlockIdRef.current = null;
      setStatusMessage('');
      setAiTipMessage('');
      setCodingActions([]);
      setCodingStats(null);
      if (!status) return;
      setBlocks((prev) =>
        prev.map((block) => {
          if (
            block.type !== 'tool-activity' ||
            !Array.isArray(block.executions)
          )
            return block;
          return {
            ...block,
            executions: block.executions.map((execution) =>
              finalizeExecutionStatus(execution, status),
            ),
          };
        }),
      );
    },
    [],
  );

  const syncHistoryFromServer = useCallback(async () => {
    for (let attempt = 1; attempt <= 3; attempt += 1) {
      try {
        const response = await fetch(historyUrl, { credentials: 'include' });
        if (!response.ok) return;
        const data = await response.json();
        if (!Array.isArray(data?.blocks)) return;
        blockIdCounter = Math.max(
          ...data.blocks.map((block: ChatBlock) => block.id),
          blockIdCounter,
        );
        setBlocks(normalizeChatBlocks(data.blocks));
        setLoadError(null);
        return;
      } catch {
        if (attempt === 3) return;
        await new Promise((resolve) => setTimeout(resolve, 150 * attempt));
      }
    }
  }, [historyUrl]);

  useEffect(() => {
    let cancelled = false;
    streamBufferRef.current = {
      type: 'agent-text',
      content: '',
      images: undefined,
    };
    toolGroupRef.current = [];
    toolActivityBlockIdRef.current = null;
    setBlocks([]);
    setLoadError(null);

    async function loadHistory() {
      const maxAttempts = 6;
      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        try {
          const response = await fetch(historyUrl, { credentials: 'include' });
          if (response.ok) {
            const data = await response.json();
            if (cancelled) return;
            if (Array.isArray(data?.blocks)) {
              blockIdCounter = Math.max(
                ...data.blocks.map((block: ChatBlock) => block.id),
                blockIdCounter,
              );
              setBlocks(normalizeChatBlocks(data.blocks));
            }
            setConnected(true);
            setLoadError(null);
            await refreshHealth();
            return;
          }

          if (
            response.status !== 502 &&
            response.status !== 503 &&
            response.status !== 504
          ) {
            setLoadError(
              t('errors.loadChatStatus', { status: response.status }),
            );
            break;
          }
        } catch {
          if (attempt === maxAttempts) {
            setLoadError(t('errors.loadChatHistory'));
            break;
          }
        }

        if (attempt < maxAttempts) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }

      if (!cancelled) {
        setConnected(false);
      }
    }

    void loadHistory();

    return () => {
      cancelled = true;
    };
  }, [activeChatSessionId, historyUrl, refreshHealth, refreshKey, t]);

  useEffect(() => {
    const interval = setInterval(() => {
      void refreshHealth();
    }, 20000);
    return () => clearInterval(interval);
  }, [refreshHealth]);

  useEffect(() => {
    if (!backendBusy || processing) return;
    const interval = setInterval(() => {
      void syncHistoryFromServer();
    }, 2500);
    return () => clearInterval(interval);
  }, [backendBusy, processing, syncHistoryFromServer]);

  useEffect(() => {
    const wasBusy = prevBackendBusyRef.current;
    if (wasBusy && !backendBusy && !processing) {
      void syncHistoryFromServer();
    }
    prevBackendBusyRef.current = backendBusy;
  }, [backendBusy, processing, syncHistoryFromServer]);

  const scheduleUpdate = useCallback(() => {
    if (updateTimerRef.current) return;
    updateTimerRef.current = setTimeout(() => {
      updateTimerRef.current = null;
      setBlocks((prev) => [...prev]);
    }, 50);
  }, []);

  const handleSSEEvent = useCallback(
    (event: string, data: Record<string, unknown>) => {
      if (event === 'output') {
        const content = String(data.content || '');
        const stream = String(data.stream || '');
        const images = Array.isArray(data.images)
          ? (data.images as ChatImage[])
          : undefined;

        if (stream === 'agent') {
          if (toolGroupRef.current.length > 0) flushToolGroup();
          if (streamBufferRef.current.type !== 'agent-text') {
            flushBuffer();
            streamBufferRef.current = {
              type: 'agent-text',
              content: '',
              images: undefined,
            };
          }
          streamBufferRef.current.content += content;
          if (images && images.length > 0)
            streamBufferRef.current.images = images;
          scheduleUpdate();
        } else if (stream === 'acpx') {
          // Internal run-note output is intentionally hidden from chat.
          return;
        }
      } else if (event === 'tool-call') {
        flushBuffer();
        const name = String(data.name || '');
        toolGroupRef.current.push(name);
        const aiStatus = String(data.status || '').trim();
        setStatusMessage(aiStatus);
        setShowLongTaskTip(true);
        scheduleUpdate();
      } else if (event === 'progress') {
        const message = String(data.message || '').trim();
        const tip = String(data.tip || '').trim();
        if (message) setStatusMessage(message);
        if (tip) setAiTipMessage(tip);
      } else if (event === 'reasoning-summary') {
        const summary = String(data.content || '')
          .trim()
          .slice(0, 500);
        if (!summary) return;
        setStatusMessage(summary);
        setBlocks((prev) => {
          const existingId = thinkingBlockIdRef.current;
          if (!existingId) {
            const nextId = ++blockIdCounter;
            thinkingBlockIdRef.current = nextId;
            return [
              ...prev,
              {
                id: nextId,
                type: 'reasoning-summary',
                content: summary,
                entries: [summary],
                collapsed: false,
              },
            ];
          }
          return prev.map((block) => {
            if (block.id !== existingId || block.type !== 'reasoning-summary')
              return block;
            const entries = block.entries || [block.content];
            if (entries[entries.length - 1] === summary) return block;
            const nextEntries = [...entries, summary].slice(-20);
            return {
              ...block,
              content: summary,
              entries: nextEntries,
              collapsed: false,
            };
          });
        });
      } else if (event === 'tool-result') {
        flushBuffer();
        flushToolGroup();
        return;
      } else if (event === 'tool-activity') {
        const execution = (data.execution || null) as ToolExecution | null;
        if (!execution?.id) return;
        setBlocks((prev) => {
          const existingId = toolActivityBlockIdRef.current;
          if (!existingId) {
            const nextId = ++blockIdCounter;
            toolActivityBlockIdRef.current = nextId;
            return [
              ...prev,
              {
                id: nextId,
                type: 'tool-activity',
                content: 'Tool activity',
                executions: [execution],
                collapsed: false,
              },
            ];
          }

          return prev.map((block) => {
            if (block.id !== existingId || block.type !== 'tool-activity')
              return block;
            const current = Array.isArray(block.executions)
              ? block.executions
              : [];
            const index = current.findIndex((item) => item.id === execution.id);
            const nextExecutions =
              index === -1
                ? [...current, execution]
                : current.map((item, currentIndex) =>
                    currentIndex === index ? execution : item,
                  );
            return {
              ...block,
              executions: nextExecutions,
            };
          });
        });
      } else if (event === 'error') {
        flushBuffer();
        flushToolGroup();
        console.error('PM chat stream error:', data.message);
        setBlocks((prev) => [
          ...prev,
          {
            id: ++blockIdCounter,
            type: 'error',
            content: t('errors.unknown'),
          },
        ]);
      } else if (event === 'advice') {
        flushBuffer();
        flushToolGroup();
        const options = Array.isArray(data.options)
          ? (data.options as AdviceOption[])
          : [];
        if (options.length > 0) {
          setBlocks((prev) => [
            ...prev,
            {
              id: ++blockIdCounter,
              type: 'advice',
              content: 'Choose one optimization option or ignore.',
              options,
            },
          ]);
        }
      } else if (event === 'coding-progress') {
        const label = String(data.label || '').trim();
        const tool = String(data.tool || '').trim();
        const stats = data.stats as
          | { reads: number; edits: number; commands: number }
          | undefined;
        if (label) {
          setCodingActions((prev) => {
            const next = [...prev, { tool, label }];
            return next.length > 20 ? next.slice(-20) : next;
          });
          setStatusMessage(label);
        }
        if (stats) setCodingStats(stats);
      } else if (event === 'done') {
        flushBuffer();
        flushToolGroup();
        const doneThinkingId = thinkingBlockIdRef.current;
        if (doneThinkingId) {
          setBlocks((prev) =>
            prev.map((block) =>
              block.id === doneThinkingId && block.type === 'reasoning-summary'
                ? { ...block, collapsed: true }
                : block,
            ),
          );
        }
        thinkingBlockIdRef.current = null;
        clearTransientActivity();
        void syncHistoryFromServer();
        void refreshHealth();
      }
    },
    [
      clearTransientActivity,
      flushBuffer,
      flushToolGroup,
      refreshHealth,
      scheduleUpdate,
      syncHistoryFromServer,
      t,
    ],
  );

  const processSSEBuffer = useCallback(
    (raw: string) => {
      const normalized = raw.replace(/\r\n/g, '\n');
      const lines = normalized.split('\n');
      let eventType = '';
      const dataLines: string[] = [];

      for (const line of lines) {
        if (!line) continue;
        if (line.startsWith('event: ')) {
          eventType = line.slice(7).trim();
        } else if (line.startsWith('data: ')) {
          dataLines.push(line.slice(6));
        }
      }

      if (!eventType || dataLines.length === 0) return;

      try {
        handleSSEEvent(eventType, JSON.parse(dataLines.join('\n')));
      } catch {
        return;
      }
    },
    [handleSSEEvent],
  );

  const sendMessage = useCallback(
    async ({ content, images }: { content: string; images: ChatImage[] }) => {
      const trimmed = content.trim();
      if (
        (!trimmed && images.length === 0) ||
        processing ||
        backendBusy ||
        sendingRef.current ||
        !connected
      ) {
        return false;
      }

      sendingRef.current = true;
      flushBuffer();
      flushToolGroup();
      thinkingBlockIdRef.current = null;
      setBlocks((prev) => [
        ...prev,
        {
          id: ++blockIdCounter,
          type: 'user',
          content: trimmed,
          images: images.length > 0 ? images : undefined,
        },
      ]);
      streamBufferRef.current = {
        type: 'agent-text',
        content: '',
        images: undefined,
      };
      setProcessing(true);
      setStatusMessage('');
      setAiTipMessage('');
      startLongTaskTipTimer();

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const payload = {
          content: trimmed || 'Analyze this image.',
          images: images.length > 0 ? images : undefined,
          chatSessionId: activeChatSessionId,
        };

        const response = await fetch(`${apiBase}/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          credentials: 'include',
          signal: controller.signal,
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          console.error('PM chat request failed:', data.error);
          setBlocks((prev) => [
            ...prev,
            {
              id: ++blockIdCounter,
              type: 'error',
              content: t('errors.request'),
            },
          ]);
          return false;
        }

        const reader = response.body?.getReader();
        if (!reader) {
          setProcessing(false);
          return false;
        }

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (value) {
            buffer += decoder.decode(value, { stream: !done });
          }

          let boundary = buffer.indexOf('\n\n');
          while (boundary !== -1) {
            const rawEvent = buffer.slice(0, boundary);
            buffer = buffer.slice(boundary + 2);
            processSSEBuffer(rawEvent);
            boundary = buffer.indexOf('\n\n');
          }

          if (done) {
            if (buffer.trim()) processSSEBuffer(buffer);
            break;
          }
        }

        return true;
      } catch (error: unknown) {
        if ((error as Error).name !== 'AbortError') {
          setBlocks((prev) => [
            ...prev,
            {
              id: ++blockIdCounter,
              type: 'error',
              content: t('errors.connection'),
            },
          ]);
        }
        return false;
      } finally {
        flushBuffer();
        flushToolGroup();
        setProcessing(false);
        resetLongTaskTip();
        abortRef.current = null;
        sendingRef.current = false;
        clearTransientActivity(
          controller.signal.aborted ? 'cancelled' : 'failed',
        );
        void syncHistoryFromServer();
        void refreshHealth();
      }
    },
    [
      apiBase,
      backendBusy,
      clearTransientActivity,
      connected,
      flushBuffer,
      flushToolGroup,
      processSSEBuffer,
      processing,
      refreshHealth,
      resetLongTaskTip,
      startLongTaskTipTimer,
      syncHistoryFromServer,
      t,
    ],
  );

  const interrupt = useCallback(async () => {
    abortRef.current?.abort();
    resetLongTaskTip();
    try {
      await fetch(`${apiBase}/interrupt`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      return;
    }
  }, [apiBase, resetLongTaskTip]);

  const toggleCollapse = useCallback((id: number) => {
    setBlocks((prev) =>
      prev.map((block) =>
        block.id === id ? { ...block, collapsed: !block.collapsed } : block,
      ),
    );
  }, []);

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4">
        {loadError ? (
          <div className="mb-4 rounded-2xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {loadError}
          </div>
        ) : null}

        {!connected && !processing && blocks.length === 0 ? (
          <div className="flex h-full min-h-[320px] flex-col items-center justify-center gap-3 text-center text-muted-foreground">
            <MessageSquareText className="h-10 w-10" />
            <div>
              <p className="font-medium text-foreground">
                Waiting for the PM agent
              </p>
              <p className="mt-1 text-sm">
                If the session just started, the orchestrator may still be
                booting.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <TalkChatBlocks
              blocks={blocks}
              streamingBuffer={streamBufferRef.current}
              pendingTools={toolGroupRef.current}
              codingActions={codingActions}
              codingStats={codingStats}
              statusMessage={statusMessage}
              processing={processing}
              backendBusy={backendBusy}
              aiTipMessage={aiTipMessage}
              onToggleCollapse={toggleCollapse}
              onSubmitPrompt={(content) => sendMessage({ content, images: [] })}
            />
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      <TalkChatComposer
        connected={connected}
        processing={processing}
        backendBusy={backendBusy}
        showLongTaskTip={showLongTaskTip}
        onSubmit={sendMessage}
        onInterrupt={interrupt}
      />
    </div>
  );
}
